import { AlertCircle, Calendar, Clock, DollarSign, Search, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRfis } from "@/hooks/use-rfis";

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "open":
      return "success";
    case "draft":
    case "pending":
      return "warning";
    case "closed":
    case "answered":
      return "secondary";
    case "rejected":
    case "void":
      return "destructive";
    default:
      return "default";
  }
}

function priorityVariant(
  priority: string | null,
): "destructive" | "warning" | "default" | "secondary" {
  switch (priority) {
    case "urgent":
    case "high":
      return "destructive";
    case "medium":
      return "warning";
    case "low":
      return "secondary";
    default:
      return "default";
  }
}

function formatDate(date: string | null): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "all" },
  { label: "Open", value: "open" },
  { label: "Draft", value: "draft" },
  { label: "Pending", value: "pending" },
  { label: "Answered", value: "answered" },
  { label: "Closed", value: "closed" },
  { label: "Rejected", value: "rejected" },
  { label: "Void", value: "void" },
] as const;

export function RfisPage() {
  const { data, isLoading, isError, error, refetch } = useRfis();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error?.message ?? "Failed to load RFIs"}</p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  const allItems = data ?? [];
  const items = allItems.filter((rfi) => {
    const matchesStatus = statusFilter === "all" || rfi.status === statusFilter;
    const matchesSearch =
      search.trim() === "" ||
      (rfi.subject ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (rfi.number ?? "").toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const selectedRfi = allItems.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">RFIs</h1>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search RFIs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No RFIs found.
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((rfi) => (
                    <TableRow
                      key={rfi.id}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        selectedId === rfi.id ? "bg-muted" : ""
                      }`}
                      onClick={() => setSelectedId(selectedId === rfi.id ? null : rfi.id)}
                    >
                      <TableCell className="font-medium">{rfi.number ?? "--"}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {rfi.subject ?? "--"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(rfi.status)}>{rfi.status ?? "--"}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={priorityVariant(rfi.priority)}>
                          {rfi.priority ?? "--"}
                        </Badge>
                      </TableCell>
                      <TableCell>{rfi.submittedBy ?? "--"}</TableCell>
                      <TableCell>{formatDate(rfi.dateSubmitted)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>

          {selectedRfi && (
            <div className="w-96 shrink-0 border rounded-lg p-4 sticky top-4 self-start space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">RFI {selectedRfi.number}</p>
                  <h2 className="text-lg font-semibold leading-tight mt-1">
                    {selectedRfi.subject ?? "Untitled"}
                  </h2>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedId(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant={statusVariant(selectedRfi.status)}>
                  {selectedRfi.status ?? "--"}
                </Badge>
                <Badge variant={priorityVariant(selectedRfi.priority)}>
                  {selectedRfi.priority ?? "--"}
                </Badge>
              </div>

              <div className="grid gap-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>Submitted by:</span>
                  <span className="font-medium text-foreground">
                    {selectedRfi.submittedBy ?? "--"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>Assigned to:</span>
                  <span className="font-medium text-foreground">
                    {selectedRfi.assignedTo ?? "--"}
                  </span>
                </div>
              </div>

              <div className="grid gap-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span>Submitted:</span>
                  <span className="text-foreground">{formatDate(selectedRfi.dateSubmitted)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span>Required by:</span>
                  <span className="text-foreground">{formatDate(selectedRfi.dateRequired)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span>Answered:</span>
                  <span className="text-foreground">{formatDate(selectedRfi.dateAnswered)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Question</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {selectedRfi.question ?? "No question text provided."}
                </p>
              </div>

              {selectedRfi.answer && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Answer</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedRfi.answer}
                  </p>
                </div>
              )}

              <div className="flex gap-4 pt-2 border-t text-sm">
                <div className="flex items-center gap-1.5">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Cost Impact:</span>
                  <span
                    className={
                      selectedRfi.costImpact === "yes"
                        ? "font-medium text-destructive"
                        : "text-foreground"
                    }
                  >
                    {selectedRfi.costImpact ?? "--"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Schedule:</span>
                  <span
                    className={
                      selectedRfi.scheduleImpact === "yes"
                        ? "font-medium text-destructive"
                        : "text-foreground"
                    }
                  >
                    {selectedRfi.scheduleImpact ?? "--"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
